(() => {
  const SELECTOR = '[data-verso-sticky-signup]';
  const MOBILE_QUERY = '(max-width: 767px)';
  const SCROLL_POSITION_KEY = `verso-signup-scroll-position:${window.location.pathname}`;

  const restoreScrollPosition = () => {
    let savedPosition;

    try {
      savedPosition = sessionStorage.getItem(SCROLL_POSITION_KEY);
      sessionStorage.removeItem(SCROLL_POSITION_KEY);
    } catch (_error) {
      return;
    }

    if (savedPosition === null) return;

    const scrollPosition = Number(savedPosition);
    if (!Number.isFinite(scrollPosition)) return;

    if (window.location.hash === '#contact_form') {
      history.replaceState(null, '', `${window.location.pathname}${window.location.search}`);
    }

    const restore = () => {
      const previousScrollBehavior = document.documentElement.style.scrollBehavior;
      document.documentElement.style.scrollBehavior = 'auto';
      window.scrollTo(0, scrollPosition);
      requestAnimationFrame(() => {
        window.scrollTo(0, scrollPosition);
        document.documentElement.style.scrollBehavior = previousScrollBehavior;
      });
    };

    restore();
    window.addEventListener('load', restore, { once: true });
  };

  const preserveScrollPosition = (scope = document) => {
    scope.querySelectorAll('.verso-attention__form').forEach((form) => {
      if (form.dataset.scrollPositionPreserved === 'true') return;

      form.dataset.scrollPositionPreserved = 'true';
      form.addEventListener('submit', () => {
        try {
          sessionStorage.setItem(SCROLL_POSITION_KEY, String(window.scrollY));
        } catch (_error) {
          return;
        }
      });
    });
  };

  class VersoStickySignup {
    constructor(root) {
      if (root.dataset.initialized === 'true') return;

      this.root = root;
      this.root.dataset.initialized = 'true';
      this.trigger = document.getElementById(root.dataset.triggerId);
      this.panel = root.querySelector('.verso-sticky-signup__panel');
      this.openButton = root.querySelector('[data-verso-sticky-open]');
      this.closeButtons = root.querySelectorAll('[data-verso-sticky-close]');
      this.dismissButton = root.querySelector('[data-verso-sticky-dismiss]');
      this.result = root.querySelector('[data-verso-sticky-result]');
      this.mobileMedia = window.matchMedia(MOBILE_QUERY);
      this.lastFocusedElement = null;
      this.ticking = false;

      if (!this.trigger || !this.panel || !this.openButton) return;
      document.body.append(this.root);

      this.handleScroll = this.handleScroll.bind(this);
      this.handleKeydown = this.handleKeydown.bind(this);
      this.handleMediaChange = this.handleMediaChange.bind(this);
      this.handleSectionUnload = this.handleSectionUnload.bind(this);

      this.openButton.addEventListener('click', () => this.openModal());
      this.closeButtons.forEach((button) => button.addEventListener('click', () => this.closeModal()));
      this.dismissButton?.addEventListener('click', () => this.dismiss());
      document.addEventListener('keydown', this.handleKeydown);
      window.addEventListener('scroll', this.handleScroll, { passive: true });
      window.addEventListener('resize', this.handleScroll, { passive: true });
      this.mobileMedia.addEventListener?.('change', this.handleMediaChange);
      document.addEventListener('shopify:section:unload', this.handleSectionUnload);

      if (this.result) {
        this.show();
        if (this.mobileMedia.matches) this.openModal();
      } else {
        this.updateVisibility();
      }
    }

    isDismissed() {
      try {
        return sessionStorage.getItem(this.root.dataset.storageKey) === 'dismissed';
      } catch (_error) {
        return false;
      }
    }

    handleScroll() {
      if (this.ticking) return;

      this.ticking = true;
      requestAnimationFrame(() => {
        this.updateVisibility();
        this.ticking = false;
      });
    }

    updateVisibility() {
      if (this.result || this.isDismissed()) return;

      const hasPassedSecondScreen = this.trigger.getBoundingClientRect().bottom <= 0;
      if (hasPassedSecondScreen) {
        this.show();
      } else {
        this.hide();
      }
    }

    show() {
      if (this.isDismissed()) return;

      this.root.hidden = false;
      requestAnimationFrame(() => this.root.classList.add('is-visible'));
      this.updatePanelAccessibility();
    }

    hide() {
      this.closeModal(false);
      this.root.classList.remove('is-visible');
      this.root.hidden = true;
      this.updatePanelAccessibility();
    }

    openModal() {
      if (!this.mobileMedia.matches || this.root.hidden) return;

      this.lastFocusedElement = document.activeElement;
      this.root.classList.add('is-open');
      this.openButton.setAttribute('aria-expanded', 'true');
      document.documentElement.classList.add('verso-sticky-modal-open');
      this.updatePanelAccessibility();
      requestAnimationFrame(() => this.panel.querySelector('input')?.focus());
    }

    closeModal(restoreFocus = true) {
      const wasOpen = this.root.classList.contains('is-open');
      this.root.classList.remove('is-open');
      this.openButton.setAttribute('aria-expanded', 'false');
      document.documentElement.classList.remove('verso-sticky-modal-open');
      this.updatePanelAccessibility();

      if (wasOpen && restoreFocus && this.lastFocusedElement instanceof HTMLElement) {
        this.lastFocusedElement.focus();
      }
    }

    dismiss() {
      try {
        sessionStorage.setItem(this.root.dataset.storageKey, 'dismissed');
      } catch (_error) {
        // The widget still closes if storage is unavailable.
      }
      this.hide();
    }

    handleKeydown(event) {
      if (!this.root.classList.contains('is-open')) return;

      if (event.key === 'Escape') {
        this.closeModal();
        return;
      }

      if (event.key === 'Tab') {
        const focusableElements = [
          ...this.panel.querySelectorAll(
            '[data-verso-sticky-close], input:not([disabled]), .verso-sticky-signup__submit:not([disabled])',
          ),
        ];
        const firstElement = focusableElements[0];
        const lastElement = focusableElements[focusableElements.length - 1];

        if (event.shiftKey && document.activeElement === firstElement) {
          event.preventDefault();
          lastElement?.focus();
        } else if (!event.shiftKey && document.activeElement === lastElement) {
          event.preventDefault();
          firstElement?.focus();
        }
      }
    }

    handleMediaChange() {
      this.closeModal(false);
      this.updatePanelAccessibility();
      this.updateVisibility();
    }

    handleSectionUnload(event) {
      if (event.target.id !== this.trigger.id) return;

      this.closeModal(false);
      document.removeEventListener('keydown', this.handleKeydown);
      window.removeEventListener('scroll', this.handleScroll);
      window.removeEventListener('resize', this.handleScroll);
      this.mobileMedia.removeEventListener?.('change', this.handleMediaChange);
      document.removeEventListener('shopify:section:unload', this.handleSectionUnload);
      this.root.remove();
    }

    updatePanelAccessibility() {
      const panelIsAvailable = !this.root.hidden && (!this.mobileMedia.matches || this.root.classList.contains('is-open'));
      this.panel.toggleAttribute('inert', !panelIsAvailable);
      this.panel.setAttribute('aria-hidden', String(!panelIsAvailable));
      this.panel.setAttribute('role', this.mobileMedia.matches ? 'dialog' : 'region');
      if (this.mobileMedia.matches) {
        this.panel.setAttribute('aria-modal', 'true');
      } else {
        this.panel.removeAttribute('aria-modal');
      }
    }
  }

  const init = (scope = document) => {
    preserveScrollPosition(scope);
    scope.querySelectorAll(SELECTOR).forEach((root) => new VersoStickySignup(root));
  };

  restoreScrollPosition();

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => init());
  } else {
    init();
  }

  document.addEventListener('shopify:section:load', (event) => init(event.target));
})();
